import { useState } from 'react'
import { Link } from 'react-router-dom'
import BookingModal from '../components/BookingModal'
import FontAwesomeIcon from '../components/FontAwesomeIcon'

export default function MountElgon() {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedExperience, setSelectedExperience] = useState('')

  const handleBooking = (experience) => {
    setSelectedExperience(experience)
    setIsModalOpen(true)
  }

  return (
    <div>
      {/* Hero Section */}
      <section
        className="relative h-80 md:h-96 bg-cover bg-center flex items-center"
        style={{
          backgroundImage: 'linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url(/images/mount-elgon-hero.jpg)',
        }}
      >
        <div className="container mx-auto px-4 text-center text-white">
          <h1 className="text-3xl md:text-5xl font-bold mb-3 font-heading">
            Mount Elgon National Park
          </h1>
          <p className="text-base md:text-lg max-w-2xl mx-auto">
            Africa's Ancient Giant - Uganda's Highest Peak
          </p>
        </div>
      </section>

      {/* Overview */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-2xl font-bold text-safari-green mb-4">Destination Overview</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Mount Elgon straddles eastern Uganda and Kenya, with Uganda's Mount Elgon National Park covering
                about 1,121 km² of the 2,151 km² massif. Its peak, Wagagai (4,321 m), is the highest point in Uganda
                and the 8th highest in Africa. An extinct volcano approximately 24 million years old, Mount Elgon once
                towered far higher than Kilimanjaro.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Erosion over millennia has carved a giant 40 km² caldera and deep canyons. The park's cool high-altitude
                forests and moorlands harbor rich wildlife and has earned UNESCO designation as a transboundary Man &
                the Biosphere Reserve. Mount Elgon offers rugged, crowd-free hiking compared to Kilimanjaro or the
                Rwenzoris, with famous features including the vast caldera, hot springs, and elephant-visited Kitum Caves.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-bold text-safari-green mb-2">Size</h3>
                  <p className="text-gray-700">1,121 km²</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-bold text-safari-green mb-2">Highest Peak</h3>
                  <p className="text-gray-700">4,321m (Wagagai)</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-bold text-safari-green mb-2">Bird Species</h3>
                  <p className="text-gray-700">300+ species</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-bold text-safari-green mb-2">UNESCO Site</h3>
                  <p className="text-gray-700">Biosphere Reserve</p>
                </div>
              </div>
            </div>
            <div>
              <img
                src="/images/mount-elgon-hero.jpg"
                alt="Mount Elgon National Park"
                className="w-full h-96 object-cover rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* How to Get There */}
      <section className="py-16 bg-safari-light">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl font-bold text-safari-green mb-8 text-center">How to Get There</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-car" className="w-6 h-6" />
                  By Road from Kampala
                </h3>
                <div className="space-y-3 text-gray-700">
                  <p><strong>Via Mbale (4-5 hours):</strong> 245 km on well-maintained highway. Continue to Sipi Falls area (additional 1 hour).</p>
                  <p><strong>Public Transport:</strong> Regular buses to Mbale, then matatus or boda-bodas to trailheads.</p>
                  <p className="text-sm italic">Road suitable for 2WD vehicles. Scenic route through agricultural highlands.</p>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-plane" className="w-6 h-6" />
                  By Air
                </h3>
                <div className="space-y-3 text-gray-700">
                  <p><strong>Soroti Airstrip:</strong> Scheduled flights from Entebbe. 2-hour drive to park.</p>
                  <p><strong>Charter Options:</strong> Private charters to small airstrips near Mbale.</p>
                  <p className="text-sm italic">Air access less common than road. Most visitors drive for scenic experience.</p>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-route" className="w-6 h-6" />
                  Eastern Circuit
                </h3>
                <div className="space-y-3 text-gray-700">
                  <p><strong>From Jinja:</strong> 3-4 hours northeast. Great combination for adventure travelers.</p>
                  <p><strong>Kenya Border:</strong> Close to border. Combine with Kenya's western attractions.</p>
                  <p className="text-sm italic">Perfect add-on to Jinja white-water rafting or eastern Uganda wildlife circuits.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Highlights */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl font-bold text-safari-green mb-8 text-center">Key Highlights</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="card group">
              <div className="relative h-32 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-mountain" className="w-16 h-16 text-white" />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Wagagai Peak</h3>
                <p className="text-gray-700">
                  Uganda's highest point at 4,321m. Multi-day trek rewards hikers with sunrise over distant ranges. Non-technical climb but strenuous due to altitude and distance.
                </p>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-32 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-water" className="w-16 h-16 text-white" />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Sipi Falls Triple Cascade</h3>
                <p className="text-gray-700">
                  Three magnificent waterfalls plunging over 100 meters. Accessible day hikes through coffee farms offer stunning views and cultural encounters with local communities.
                </p>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-32 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-circle-notch" className="w-16 h-16 text-white" />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">World's Largest Caldera</h3>
                <p className="text-gray-700">
                  40 km² volcanic caldera with dramatic "Wall of Death" cliffs and hidden lakes. Jackson's Pool hot spring creates a surreal geothermal landscape inside.
                </p>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-32 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-mug-hot" className="w-16 h-16 text-white" />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Arabica Coffee Farms</h3>
                <p className="text-gray-700">
                  Mountain slopes covered in specialty coffee farms. Walk through terraced plantations, learn bean-to-cup processes, and enjoy fresh mountain-grown coffee.
                </p>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-32 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-skull-crossbones" className="w-16 h-16 text-white" />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Ancient Cave Systems</h3>
                <p className="text-gray-700">
                  Lava tube caves including famous Kitum Cave where elephants mine salt. Guided tours reveal underground waterfalls, bat colonies, and unique formations.
                </p>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-32 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-users" className="w-16 h-16 text-white" />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Sabiny Cultural Heritage</h3>
                <p className="text-gray-700">
                  Rich indigenous Sabiny and Bagisu cultures. Experience traditional ceremonies, homestays, and learn about ancient mountain farming techniques.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Activities & Experiences */}
      <section className="py-16 bg-safari-light">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl font-bold text-safari-green mb-8 text-center">Activities & Experiences</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="card group">
              <div className="relative h-40 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-mountain" className="w-20 h-20 text-white" />
              </div>
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-lg font-bold text-safari-green mb-2">Wagagai Peak Multi-Day Trek</h3>
                <p className="text-gray-700 mb-4 flex-grow">
                  Summit Uganda's second-highest peak at 4,321m via Sasa, Piswa, or Sipi trails. Explore the world's largest volcanic caldera with stunning views.
                </p>
                <div className="space-y-2 mb-4 text-sm text-gray-600">
                  <p><FontAwesomeIcon icon="fa-clock" className="w-4 h-4 mr-2" />Duration: 4-6 days</p>
                  <p><FontAwesomeIcon icon="fa-chart-simple" className="w-4 h-4 mr-2" />Difficulty: Very Strenuous</p>
                </div>
                <p className="text-safari-gold font-bold mb-3 text-center text-lg">From $400 per person</p>
                <Link to="/activities/mount-elgon-trekking" className="btn-primary w-full text-center block">
                  View Full Details
                </Link>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-40 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-person-falling" className="w-20 h-20 text-white" />
              </div>
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-lg font-bold text-safari-green mb-2">Sipi Falls Abseiling</h3>
                <p className="text-gray-700 mb-4 flex-grow">
                  Rappel down 100-meter waterfall faces in this adrenaline-pumping adventure. Professional guides and equipment provided. No experience needed.
                </p>
                <div className="space-y-2 mb-4 text-sm text-gray-600">
                  <p><FontAwesomeIcon icon="fa-clock" className="w-4 h-4 mr-2" />Duration: 2-3 hours</p>
                  <p><FontAwesomeIcon icon="fa-chart-simple" className="w-4 h-4 mr-2" />Difficulty: Strenuous</p>
                </div>
                <p className="text-safari-gold font-bold mb-3 text-center text-lg">From $100 per person</p>
                <Link to="/activities/sipi-falls-abseiling" className="btn-primary w-full text-center block">
                  View Experience
                </Link>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-40 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-hiking" className="w-20 h-20 text-white" />
              </div>
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-lg font-bold text-safari-green mb-2">Sipi Falls Hiking Trail</h3>
                <p className="text-gray-700 mb-4 flex-grow">
                  Trek to three magnificent waterfalls with optional rock climbing. Stunning scenery and local coffee farms en route.
                </p>
                <div className="space-y-2 mb-4 text-sm text-gray-600">
                  <p><FontAwesomeIcon icon="fa-clock" className="w-4 h-4 mr-2" />Duration: 3-4 hours</p>
                  <p><FontAwesomeIcon icon="fa-chart-simple" className="w-4 h-4 mr-2" />Difficulty: Moderate</p>
                </div>
                <p className="text-safari-gold font-bold mb-3 text-center text-lg">From $30 per person</p>
                <button onClick={() => handleBooking('Sipi Falls Hiking Trail - Mount Elgon')} className="btn-primary w-full">
                  Book Experience
                </button>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-40 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-skull-crossbones" className="w-20 h-20 text-white" />
              </div>
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-lg font-bold text-safari-green mb-2">Cave Exploration</h3>
                <p className="text-gray-700 mb-4 flex-grow">
                  Guided tours of ancient lava tube caves with bats, underground waterfalls, and unique formations. Headlamps and guides provided.
                </p>
                <div className="space-y-2 mb-4 text-sm text-gray-600">
                  <p><FontAwesomeIcon icon="fa-clock" className="w-4 h-4 mr-2" />Duration: 2-4 hours</p>
                  <p><FontAwesomeIcon icon="fa-chart-simple" className="w-4 h-4 mr-2" />Difficulty: Moderate</p>
                </div>
                <p className="text-safari-gold font-bold mb-3 text-center text-lg">From $40 per person</p>
                <button onClick={() => handleBooking('Cave Exploration - Mount Elgon')} className="btn-primary w-full">
                  Book Experience
                </button>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-40 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-mug-hot" className="w-20 h-20 text-white" />
              </div>
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-lg font-bold text-safari-green mb-2">Coffee Farm Hikes</h3>
                <p className="text-gray-700 mb-4 flex-grow">
                  Walk through arabica coffee farms in the foothills. Learn the bean-to-cup process and enjoy fresh coffee with spectacular mountain views.
                </p>
                <div className="space-y-2 mb-4 text-sm text-gray-600">
                  <p><FontAwesomeIcon icon="fa-clock" className="w-4 h-4 mr-2" />Duration: 2-3 hours</p>
                  <p><FontAwesomeIcon icon="fa-chart-simple" className="w-4 h-4 mr-2" />Difficulty: Easy to Moderate</p>
                </div>
                <p className="text-safari-gold font-bold mb-3 text-center text-lg">From $25 per person</p>
                <button onClick={() => handleBooking('Coffee Farm Hikes - Mount Elgon')} className="btn-primary w-full">
                  Book Experience
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Travel Tips */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl font-bold text-safari-green mb-8 text-center">Travel Tips</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-calendar" className="w-6 h-6" />
                  Best Time to Visit
                </h3>
                <div className="space-y-3 text-gray-700">
                  <p><strong>Dry Seasons (June-August, December-February):</strong> Best for summit treks and dry trails. Clear mountain views and less mud.</p>
                  <p><strong>Wet Seasons (March-May, September-November):</strong> Lush scenery, waterfalls at full power. Trails can be slippery.</p>
                  <p className="text-sm italic">Summit treks require 4-6 days. Sipi Falls accessible year-round. Plan accordingly.</p>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-suitcase" className="w-6 h-6" />
                  What to Pack
                </h3>
                <div className="space-y-2 text-gray-700">
                  <p><strong>For Summit Treks:</strong> Warm layers, waterproof jacket, thermal underwear, sleeping bag (-10°C rated), hiking boots</p>
                  <p><strong>For Day Hikes:</strong> Light hiking shoes, rain jacket, sun protection, water bottle, snacks</p>
                  <p><strong>For Abseiling:</strong> Closed-toe shoes, comfortable clothes. All technical gear provided.</p>
                  <p className="text-sm italic">Temperatures range from tropical (base) to freezing (summit). Layer appropriately.</p>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-lightbulb" className="w-6 h-6" />
                  Insider Tips
                </h3>
                <div className="space-y-2 text-gray-700">
                  <p><strong>Summit Routes:</strong> Sasa route is steepest (4 days), Sipi is scenic (5 days), Piswa is gentlest (7 days).</p>
                  <p><strong>Acclimatization:</strong> Spend a night in Sipi before summit attempts. Reduce altitude sickness risk.</p>
                  <p><strong>Porters:</strong> Hiring local porters supports community and makes trek much easier.</p>
                  <p><strong>Sipi Falls:</strong> Visit all three falls. Each offers different perspectives and photo opportunities.</p>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-coins" className="w-6 h-6" />
                  Budgeting Tips
                </h3>
                <div className="space-y-2 text-gray-700">
                  <p><strong>Park Entry:</strong> Foreign adults $35/day for summit treks. Day visits lower rates.</p>
                  <p><strong>Summit Trek Package:</strong> $400-600 includes guides, porters, park fees, mountain huts.</p>
                  <p><strong>Sipi Activities:</strong> Falls hike $30, abseiling $100, coffee tour $25.</p>
                  <p><strong>Accommodation:</strong> Budget guesthouses to luxury lodges ($15-$150+ per night in Sipi area).</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Where to Stay */}
      <section className="py-16 bg-safari-light">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl font-bold text-safari-green mb-6 text-center">Where to Stay</h2>
          <p className="text-center text-gray-700 mb-12 max-w-3xl mx-auto">
            Accommodation concentrated around Sipi Falls area, with options ranging from budget backpacker hostels to comfortable mountain lodges.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-lg shadow-lg">
              <thead className="bg-safari-green text-white">
                <tr>
                  <th className="px-6 py-4 text-left">Category</th>
                  <th className="px-6 py-4 text-left">Accommodation Name</th>
                  <th className="px-6 py-4 text-left">Location</th>
                  <th className="px-6 py-4 text-left">Features</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-bold text-safari-green">Luxury</td>
                  <td className="px-6 py-4">Sipi River Lodge</td>
                  <td className="px-6 py-4">Sipi Falls</td>
                  <td className="px-6 py-4">Luxury cottages, waterfall views, fine dining, infinity pool</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-bold text-safari-green">Mid-Range</td>
                  <td className="px-6 py-4">Sipi Falls Resort</td>
                  <td className="px-6 py-4">Near Sipi Falls</td>
                  <td className="px-6 py-4">Comfortable rooms, restaurant, spectacular views, activity center</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-bold text-safari-green">Mid-Range</td>
                  <td className="px-6 py-4">Crow's Nest Camp</td>
                  <td className="px-6 py-4">Sipi cliff edge</td>
                  <td className="px-6 py-4">Safari tents, panoramic views, organic farm, eco-friendly</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-bold text-safari-green">Budget</td>
                  <td className="px-6 py-4">Moses Campsite</td>
                  <td className="px-6 py-4">Sipi village</td>
                  <td className="px-6 py-4">Budget rooms, camping, backpacker favorite, community-run</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-bold text-safari-green">Budget</td>
                  <td className="px-6 py-4">Sipi Valley Resort</td>
                  <td className="px-6 py-4">Sipi area</td>
                  <td className="px-6 py-4">Basic bandas, camping available, affordable, local atmosphere</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-bold text-safari-green">Summit Treks</td>
                  <td className="px-6 py-4">Mountain Huts</td>
                  <td className="px-6 py-4">Various trail locations</td>
                  <td className="px-6 py-4">Basic shelters on Wagagai routes, bunk beds, shared facilities</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl font-bold text-safari-green mb-8 text-center">Frequently Asked Questions</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Is the Wagagai summit trek suitable for beginners?</h3>
                <p className="text-gray-700">
                  Moderate fitness required. Not technical but strenuous due to altitude (4,321m) and distance. Good preparation essential. First-time trekkers can succeed with proper pacing and acclimatization.
                </p>
              </div>
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Which summit route should I choose?</h3>
                <p className="text-gray-700">
                  Sasa route: Steepest, 4 days, most challenging. Sipi route: Scenic, 5 days, moderate. Piswa route: Gentlest gradient, 7 days, best for acclimatization. All reach same summit.
                </p>
              </div>
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Is abseiling at Sipi Falls safe for beginners?</h3>
                <p className="text-gray-700">
                  Yes! Professional guides provide full instruction and equipment. No prior experience needed. Safety record excellent. Minimum age typically 12 years. Not recommended if you fear heights.
                </p>
              </div>
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Can I do day hikes without a guide?</h3>
                <p className="text-gray-700">
                  Local guides required for all park activities including Sipi Falls hikes. Guides are knowledgeable, affordable, and their fees support local communities. Highly recommended even if not mandatory.
                </p>
              </div>
            </div>
            <div className="space-y-6">
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">What wildlife might I see?</h3>
                <p className="text-gray-700">
                  Less wildlife than savanna parks. Possible sightings: monkeys, duikers, elephants (near caves), various bird species. Focus is on scenery, hiking, and adventure rather than game viewing.
                </p>
              </div>
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">How cold does it get at the summit?</h3>
                <p className="text-gray-700">
                  Summit temperatures can drop below freezing, especially at night. Lower slopes are warm/tropical. Pack warm layers, thermal underwear, and -10°C rated sleeping bag for summit treks.
                </p>
              </div>
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Is Mount Elgon less crowded than Kilimanjaro?</h3>
                <p className="text-gray-700">
                  Much less crowded! While Kilimanjaro sees 50,000+ climbers yearly, Elgon gets few thousand. Enjoy solitude, authentic experience, and lower costs. Summit still rewarding at 4,321m.
                </p>
              </div>
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Can I combine Mount Elgon with other destinations?</h3>
                <p className="text-gray-700">
                  Excellent combination with Jinja (3-4 hours) for white-water rafting. Also pairs well with eastern Uganda parks or continuing to Kenya. Sipi Falls makes great 2-3 day add-on to any itinerary.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-safari-green text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Conquer Uganda's Highest Peak
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Challenge yourself on Mount Elgon's ancient trails and discover one of Africa's most underrated adventure destinations.
            From thundering waterfalls to silent moorlands, Elgon offers an authentic mountaineering experience far from the crowds.
          </p>
          <Link
            to="/plan-your-trip"
            className="btn-primary text-lg inline-block"
          >
            Plan Your Mount Elgon Adventure
          </Link>
        </div>
      </section>

      <BookingModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        experience={selectedExperience}
      />
    </div>
  )
}
